import { analyticsDebugLog, logAnalytic, logToGoogleAnalytics } from './analytics';
import {
  AgilysysDataLayerBookPageDataEvent,
  CurrencyValues, EngageValues,
  SiteInfo, SynxisDataLayerCheckoutEvent, SynxisDataLayerProduct, TrackedInfo,
} from './interfaces';
import { getBrowserName, convertDateToBigqueryFormat, isNumber } from './utils';
import { config } from './config';

// Extend the Window interface to include sbe_logMetrics
declare global {
  interface Window {
    sbe_logMetrics?: any;
  }
}

/**
 * Gets the site information from siteMetaData
 * @param siteId {string} The site ID to fetch information for
 * @returns {Promise<SiteInfo | null>} The site information or null if there was an error
 */
export async function fetchSiteInformation(
  siteId: string | null,
  hotelParam: string | null,
): Promise<SiteInfo | null> {
  try {
    const response = await fetch(`${config.HOTEL_PARAM_WORKAROUND.includes(hotelParam) ? config.HIO_API_ENDPOINT_WWW : config.HIO_API_ENDPOINT}/api/booking/getSiteInfo`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        siteId,
        hotelParam,
      }),
    });

    const jsonResponse = await response.json();
    return jsonResponse.siteInfo;
  } catch (e) {
    console.log(e);
    return null;
  }
}

export function getCookieExpireDate(): string {
  const browserType = getBrowserName();
  const date = new Date();
  let days = 30;
  if (browserType === 'safari') {
    days = 7;
  }
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000)); // days converted to milliseconds
  const expires = `expires=${date.toUTCString()}`;
  return expires;
}

export function setCookie(name: string, value: string | number | boolean) {
  const expires = getCookieExpireDate();
  document.cookie = `${name}=${value}; ${expires}; Secure; path=/`;
  // assoociated with above cookie and will tell us the date of the above cookie
  document.cookie = `${name}SetDate=${new Date()}; Secure; ${expires}; path=/`;
}

/**
 * Debug log for data layer events
 * @param args Arguments to log
 */
function dataLayerDebugLog(...args: any[]): void {
  if (localStorage.getItem('dataLayerDebugLog') === 'true') {
    console.log.apply(null, args);
  }
}

// Gets a specific cookie value
// Based on https://stackoverflow.com/a/16061433
export function getCookie(cookieName: string): string | number | boolean | Date {
  let name: string[] | null;
  const cookieValue: string | null = (name = (`${document.cookie};`).match(new RegExp(`${cookieName}=.*;`))) && name[0].split(/=|;/)[1];
  if (cookieValue === null) {
    if (cookieName.endsWith('SetDate')) {
      return null;
    }
    return '';
  }
  if (cookieValue === 'true') {
    return true;
  }
  if (cookieValue === 'false') {
    return false;
  }
  if (cookieName.endsWith('SetDate')) {
    return new Date(cookieValue);
  }
  if (isNumber(cookieValue)) {
    return Number(cookieValue);
  }
  return cookieValue;
}

export async function markScriptInstalled(
  siteId: string,
  hotelParam: string | null,
): Promise<void> {
  try {
    await fetch(`${config.HOTEL_PARAM_WORKAROUND.includes(hotelParam) ? config.HIO_API_ENDPOINT_WWW : config.HIO_API_ENDPOINT}/api/booking/markScriptInstalled`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        siteId,
      }),
    });
  } catch (e) {
    console.log(e);
  }
}

// check for hovr tracking parameter for direct CTA Click
// and store a cookie for later check outs
export function checkForDirectCTA() {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const urlParams = Object.fromEntries(urlSearchParams.entries());

  if (urlParams.hovrDirect != null) {
    // direct cta cick
    setCookie('hovrDirectCTA', true);
  }
}

const convertCurrencyToUSD = (currency: string, value: number): number => {
  const conversionRates = {
    '€': 1.1, // Example conversion rate: 1 EUR = 1.1 USD
    EUR: 1.1,
    '£': 1.3, // Example conversion rate: 1 GBP = 1.3 USD
    GBP: 1.3,
    $: 1, // USD is the base currency
    USD: 1,
  };
  return value * (conversionRates[currency] || 1);
};

const parseCurrency = (str: string): number => {
  const currencyMatch = str.match(/(EUR|GBP|USD|€|£|\$)/);
  const currency = currencyMatch ? currencyMatch[0] : 'USD';

  const raw = str.replace(/[^0-9.,-]/g, '');

  const hasComma = raw.includes(',');
  const hasDot = raw.includes('.');

  let normalized = raw;

  if (hasComma && hasDot) {
    // EU format: "1.234,56"
    if (raw.indexOf(',') > raw.indexOf('.')) {
      normalized = raw.replace(/\./g, '').replace(',', '.'); // EU
    } else {
      normalized = raw.replace(/,/g, ''); // US
    }
  } else if (hasComma && !hasDot) {
    // Case like "$999,000" → comma is thousands separator
    // NOT decimal: remove all commas
    normalized = raw.replace(/,/g, '');
  } else if (!hasComma && hasDot) {
    // Standard US decimal
    normalized = raw;
  }

  const value = parseFloat(normalized);
  return convertCurrencyToUSD(currency, value);
};

// Determines the booking engine, and if supported reads the dataLayer for currency values
function checkDataLayerForCurrencyValues() {
  // @ts-ignore
  const { dataLayer } = window;
  if (!dataLayer) {
    return null;
  }
  // eslint-disable-next-line guard-for-in, no-restricted-syntax
  for (const i in dataLayer) {
    const item = dataLayer[i];
    if (item?.event === 'checkoutLoad') { // If on the checkout page
      const event: SynxisDataLayerCheckoutEvent = dataLayer[i];
      const eventProducts = event.ecommerce.checkout.products;
      const priceTotal: number = eventProducts.reduce((
        sum: number,
        product: SynxisDataLayerProduct,
      ) => sum + (product.price * product.quantity), 0);
      const priceCurrency: string = event.ecommerce.currency; // 'USD', 'EUR', 'GBP', etc.
      const parsedTotal: number = convertCurrencyToUSD(priceCurrency, priceTotal);
      return parsedTotal;
    } if (item?.event === 'bookPageData') { // If on the checkout page
      const event: AgilysysDataLayerBookPageDataEvent = dataLayer[i];
      const priceTotal: number = event.value;
      const priceCurrency: string = event.Currency; // 'USD', 'EUR', 'GBP', etc.
      const parsedTotal: number = convertCurrencyToUSD(priceCurrency, priceTotal);
      return parsedTotal;
    } if (item?.event === 'cen-transaction') { // If on the confirmation page for cendyn
      const event = dataLayer[i];
      dataLayerDebugLog('cen-transaction event: ', event);
      const priceTotal: number = event.cen_RezTotalrevenueWithoutTax;
      const priceCurrency: string = event.cen_RezCurrency; // 'USD', 'EUR', 'GBP', etc.
      const parsedTotal: number = convertCurrencyToUSD(priceCurrency, priceTotal);
      return parsedTotal;
    } if (item?.event === 'dl_purchase' && item?.ecommerce != null) {
      // standard ecommerce purchase event
      const { price } = item.ecommerce.items[0];
      const { quantity } = item.ecommerce.items[0];
      if (price != null && quantity != null) {
        const priceTotal: number = price * quantity;
        const priceCurrency: string = item.ecommerce.items[0].currency || 'USD'; // 'USD', 'EUR', 'GBP', etc.
        const parsedTotal: number = convertCurrencyToUSD(priceCurrency, priceTotal);
        return parsedTotal;
      }
    } else if (item?.event === 'confirmation' && item?.ecommerce != null) {
      const price = item.ecommerce.purchase.actionField?.revenue;
      const priceCurrency = item.ecommerce.currencyCode || 'USD'; // 'USD', 'EUR', 'GBP', etc.
      const parsedTotal: number = convertCurrencyToUSD(priceCurrency, price);
      return parsedTotal;
    } else if (item?.event === 'ga_4_purchase' && item?.ecommerce != null) {
      // ihotelier ga4
      const price = item.ecommerce.value ?? 0;
      const priceCurrency = item.ecommerce.currency || 'USD'; // 'USD', 'EUR', 'GBP', etc.
      const parsedTotal: number = convertCurrencyToUSD(priceCurrency, price);
      return parsedTotal;
    } else {
      const eventKeys = Object.keys(item);
      if (eventKeys.includes('TotalCostWithTax')) {
        const priceTotal: number = item.TotalCostWithTax;
        // 'USD', 'EUR', 'GBP', etc.
        const priceCurrency: string = item?.Currency || item?.CurrCode || 'USD';
        const parsedTotal: number = convertCurrencyToUSD(priceCurrency, priceTotal);
        return parsedTotal;
      }
    }
  }
  return null;
}

function resetLocalStorageData(): void {
  localStorage.removeItem('hovrEngaged');
  localStorage.removeItem('hovrEngagedDate');
  localStorage.removeItem('dollarValues');
  localStorage.removeItem('hovrSessionId');
  localStorage.removeItem('hovrThirtyDayTrackingID');
  localStorage.removeItem('siteId');
}

interface LocalStorageData {
  hovrEngaged: boolean | null;
  hovrEngagedDate: Date | null;
  dollarValues: CurrencyValues | null;
  hovrSessionId: string | null;
  thirtyDayTrackingID: string | null;
  siteId: string | null;
}

function getDataFromLocalStorage(): LocalStorageData | null {
  const hovrEngaged = localStorage.getItem('hovrEngaged');
  const hovrEngagedDate = localStorage.getItem('hovrEngagedDate');
  const siteId = localStorage.getItem('siteId');
  const hovrSessionId = localStorage.getItem('hovrSessionId');
  const dollarValues = localStorage.getItem('dollarValues');
  const thirtyDayTrackingID = localStorage.getItem('hovrThirtyDayTrackingID');
  const returnData: LocalStorageData = {
    hovrEngaged: null,
    hovrEngagedDate: null,
    dollarValues: null,
    hovrSessionId: null,
    thirtyDayTrackingID: null,
    siteId: null,
  };
  let hasData = false;
  if (hovrEngaged) {
    hasData = true;
    returnData.hovrEngaged = true;
  }
  if (hovrEngagedDate) {
    hasData = true;
    returnData.hovrEngagedDate = new Date(hovrEngagedDate);
  }
  if (dollarValues) {
    hasData = true;
    returnData.dollarValues = JSON.parse(dollarValues);
  }
  if (hovrSessionId) {
    hasData = true;
    returnData.hovrSessionId = hovrSessionId;
  }
  if (thirtyDayTrackingID) {
    hasData = true;
    returnData.thirtyDayTrackingID = thirtyDayTrackingID;
  }
  if (siteId) {
    hasData = true;
    returnData.siteId = siteId;
  }
  if (hasData) {
    return returnData;
  }
  return null;
}

function getTotalAmount(): number | null {
  try {
    // Get all text content on the page
    const pageText = document.body.innerText;

    // Regular expression to match "Total" but ignore "Subtotal"
    // Handles spaces after currency symbols as well
    const regex = /(?<!Sub)Total\s*\$?\s*([\d,]+\.?\d{0,2})/i;
    const match = pageText.match(regex);

    // Return the extracted amount as a number, or null if not found
    return match ? parseFloat(match[1].replace(/,/g, '')) : null;
  } catch (error) {
    analyticsDebugLog('Error in getTotalAmount(): ', error);
    return null;
  }
}

async function checkDataLayerForCurrencyValuesWithWait(
  tryCount = 0,
): Promise<CurrencyValues | null> {
  const retries = 3; // Number of retries to check the data layer
  const waitTime = 1500;
  const dataLayerValue = checkDataLayerForCurrencyValues();
  if (dataLayerValue) {
    console.log('data layer instant check');
    return {
      highestValue: dataLayerValue,
      dollarArray: [dataLayerValue],
    };
  }
  await new Promise((resolve) => { setTimeout(resolve, waitTime); });
  console.log('data layer wait check: ', tryCount);
  const retryLayerValue = checkDataLayerForCurrencyValues();
  if (retryLayerValue) {
    return {
      highestValue: retryLayerValue,
      dollarArray: [retryLayerValue],
    };
  }

  if (tryCount >= retries) {
    return null;
  }
  return checkDataLayerForCurrencyValuesWithWait(tryCount + 1);
}

// Synchronous version for use with MutationObserver (no retries/waiting)
function findCurrencyValuesSync(): CurrencyValues | null {
  const totalAmount: number | null = getTotalAmount();
  if (totalAmount != null) {
    return {
      highestValue: totalAmount,
      dollarArray: [totalAmount],
    };
  }

  // eslint-disable-next-line no-useless-escape
  const currencyRegex = /\$\d{1,3}(?:,\d{3})*(?:\.\d{2})?|\€\d{1,3}(?:\.\d{3})*(?:,\d{2})?|\£\d{1,3}(?:,\d{3})*(?:\.\d{2})?/g;
  const text = document.body.innerText;
  const matches = text.match(currencyRegex);

  if (matches) {
    const values: number[] = matches.map(parseCurrency);
    const highestValue: number = Math.max(...values);
    return {
      highestValue,
      dollarArray: values,
    };
  }
  return null;
}

// Order of priority:
// 1. Data layer
// 2. getTotalAmount()
// 3. old regex logic of using highest value
async function findCurrencyValues(): Promise<CurrencyValues | null> {
  // We prefer using the dataLayer if available
  try {
    const dataLayerValue = await checkDataLayerForCurrencyValuesWithWait();
    if (dataLayerValue) {
      dataLayerDebugLog('Found data layer value: ', dataLayerValue);
      return dataLayerValue;
    }
  } catch (error) {
    dataLayerDebugLog('Error checking data layer for currency values: ', error);
  }
  const totalAmount: number | null = getTotalAmount();

  if (totalAmount != null) {
    return {
      highestValue: totalAmount,
      dollarArray: [totalAmount],
    };
  }

  // Updated regex to match USD, EUR, and GBP formats
  // eslint-disable-next-line no-useless-escape
  const currencyRegex = /\$\d{1,3}(?:,\d{3})*(?:\.\d{2})?|\€\d{1,3}(?:\.\d{3})*(?:,\d{2})?|\£\d{1,3}(?:,\d{3})*(?:\.\d{2})?/g;
  const text = document.body.innerText;
  const matches = text.match(currencyRegex);

  if (matches) {
    // Convert all currency strings to USD values
    const values: number[] = matches.map(parseCurrency);

    // Find and return the maximum value
    const highestValue: number = Math.max(...values);
    return {
      highestValue,
      dollarArray: values,
    };
  }
  return null;
}

function safeGetDollarValue(dollarValues: CurrencyValues, valueName: string): any {
  if (dollarValues == null) {
    if (valueName === 'dollarArray') {
      return [0];
    }
    return 0;
  }
  if (dollarValues[valueName] == null) {
    if (valueName === 'dollarArray') {
      return [0];
    }
    return 0;
  }
  return dollarValues[valueName];
}

/**
 * Checks if a date is valid
 * @param date {Date} Date to check if it is valid
 * @returns {boolean} True if the date is valid, false otherwise
 */
function dateIsValid(date: Date): boolean {
  return !isNaN(date.getTime());
}

function scrapeCurrencyFromPage(): string {
  const counts: Record<string, number> = {};
  const bump = (code: string) => { counts[code] = (counts[code] || 0) + 1; };
  try {
    // 1. <meta> tags (common in booking / e-commerce sites)
    const metaSelectors = [
      'meta[property="og:price:currency"]',
      'meta[name="currency"]',
      'meta[itemprop="priceCurrency"]',
      'meta[property="product:price:currency"]',
    ];
    for (const selector of metaSelectors) {
      const el = document.querySelector(selector) as HTMLMetaElement | null;
      if (el?.content) bump(el.content.trim().toUpperCase());
    }

    // 2. Microdata / schema.org priceCurrency attributes
    const priceCurrencyEl = document.querySelector('[itemprop="priceCurrency"]');
    if (priceCurrencyEl) {
      const val = priceCurrencyEl.getAttribute('content') || priceCurrencyEl.textContent;
      if (val) bump(val.trim().toUpperCase());
    }

    // 3. data-currency attributes on html / body / common wrappers
    const dataAttr = document.documentElement.getAttribute('data-currency')
      || document.body?.getAttribute('data-currency');
    if (dataAttr) bump(dataAttr.trim().toUpperCase());

    // 4. Visible currency symbols / codes on the page
    const priceSelectors = [
      '[class*="price"]', '[class*="total"]', '[class*="amount"]',
      '[class*="cost"]', '[class*="rate"]', '[class*="currency"]',
      '[data-price]', '[data-total]',
    ];
    const currencyPattern = /(?:USD|EUR|GBP|CAD|AUD|MXN|CHF|JPY|CNY|BRL|NZD|SEK|NOK|DKK|COP|ARS|PEN|CLP)(?![A-Z])/gi;
    const symbolMap: Record<string, string> = { $: 'USD', '€': 'EUR', '£': 'GBP', '¥': 'JPY' };

    for (const sel of priceSelectors) {
      const els = document.querySelectorAll(sel);
      for (const el of els) {
        const text = el.textContent || '';
        const codeMatches = text.match(currencyPattern);
        if (codeMatches) {
          codeMatches.forEach((m: string) => bump(m.toUpperCase()));
        }
        for (const [symbol, code] of Object.entries(symbolMap)) {
          if (text.includes(symbol)) bump(code);
        }
      }
    }
  } catch (e) {
    // Silently fail — currency detection is best-effort
  }

  // Return the most frequently occurring currency on the page
  const entries = Object.entries(counts);
  if (entries.length === 0) return '';
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}

function getLatestCurrency() {
  let currency = '';
  // @ts-ignore
  const dataLayer = window.dataLayer || [];
  dataLayer.forEach((entry: any) => {
    if (entry.currency) {
      currency = entry.currency; // GA4 often uses "currency"
    }

    // GA4 ecommerce format:
    if (entry.ecommerce?.currency) {
      currency = entry.ecommerce.currency;
    }

    // Some sites store it per-item
    if (entry.ecommerce?.items?.[0]?.currency) {
      currency = entry.ecommerce.items[0].currency;
    }

    if (entry?.userCurrencyCode) {
      currency = entry.userCurrencyCode; // Some sites might use this field
    }
  });

  // Fallback: scrape the page for currency indicators
  if (!currency) {
    currency = scrapeCurrencyFromPage();
  }

  return currency;
}

/**
 * If the confirmation page is loaded we are going to check if
 * there are cookies set and then take credit for a sale
 */
export async function handleConfirmationPage(
  siteInfo: SiteInfo,
  engageValues: EngageValues,
): Promise<void> {
  analyticsDebugLog('handleConfirmationPage() engageValues: ', engageValues);
  // pull siteInfo and check if we are on the confirmation page
  logAnalytic(
    {
      category: 'confirmation_pageload',
    },
    siteInfo,
  );

  try {
    const dollarValues = await findCurrencyValues();
    const currency = getLatestCurrency();
    const localData: LocalStorageData = getDataFromLocalStorage();
    // check to see if there is a hovr interacted cookie
    // set in this embed - we are listening for the cta paramter we
    // add
    const directCTACookie: boolean = getCookie('hovrDirectCTA') as boolean;
    const directCTACookieSetDate: Date = getCookie('hovrDirectCTASetDate') as Date;

    let engagedValue = false;
    let engagedDate = null;
    let hovrSessionId = null;
    let thirtyDayTrackingID = null;
    let siteId = null;

    /**
       * engageValues will only be populated as true coming from the postMessage
       * listener because we check on the fly there
       *
       * the second half of the if else statement is for the case where we are on the
       * the confirmation page - which is being directly called from inedx.ts
       */
    if (engageValues.hovrEngaged) {
      engagedValue = engageValues.hovrEngaged;
      engagedDate = engageValues.hovrEngagedDate;
      siteId = engageValues.siteId;
      hovrSessionId = engageValues.hovrSessionId;
      thirtyDayTrackingID = engageValues.thirtyDayTrackingID;
    } else if (localData?.hovrEngaged) {
      engagedValue = localData?.hovrEngaged;
      engagedDate = localData?.hovrEngagedDate;
      siteId = localData.siteId;
      hovrSessionId = localData.hovrSessionId;
      thirtyDayTrackingID = localData?.thirtyDayTrackingID;
    }

    if (engagedDate != null && engagedDate !== '') {
      const originalEngagedDate = engagedDate;
      engagedDate = new Date(engagedDate);
      if (!dateIsValid(engagedDate)) {
        analyticsDebugLog('handleConfirmationPage() engagedDate is not valid, setting to null, source value: ', originalEngagedDate);
        engagedDate = null;
      }
    }

    // Debug logging
    analyticsDebugLog('handleConfirmationPage() dollarValues: ', dollarValues);
    analyticsDebugLog('handleConfirmationPage() engagedDate: ', engagedDate);
    analyticsDebugLog('handleConfirmationPage() directCTACookie: ', directCTACookie);
    analyticsDebugLog('handleConfirmationPage() directCTACookieSetDate: ', directCTACookieSetDate);
    // ==================

    if (directCTACookie === true) {
      if (directCTACookieSetDate !== null) {
        analyticsDebugLog('handleConfirmationPage() path: 1');
        // we found a direct CTA cookie value
        resetLocalStorageData();
        logAnalytic(
          {
            category: 'direct_cta_conversion_sale',
            potentialTotalValue: safeGetDollarValue(dollarValues, 'highestValue'),
            allDollarValuesOnPage: safeGetDollarValue(dollarValues, 'dollarArray'),
            interactedDate: convertDateToBigqueryFormat(directCTACookieSetDate),
            currency: currency || 'USD',
            sessionID: hovrSessionId,
            thirtyDayTrackingID,
            backupSiteId: siteId,
          },
          siteInfo,
        );
      } else {
        analyticsDebugLog('handleConfirmationPage() path: 2');
        // we found a direct CTA cookie value
        resetLocalStorageData();
        logAnalytic(
          {
            category: 'direct_cta_conversion_sale',
            potentialTotalValue: safeGetDollarValue(dollarValues, 'highestValue'),
            allDollarValuesOnPage: safeGetDollarValue(dollarValues, 'dollarArray'),
            sessionID: hovrSessionId,
            currency: currency || 'USD',
            thirtyDayTrackingID,
            backupSiteId: siteId,
          },
          siteInfo,
        );
      }
    } else if (engagedValue === true) {
      if (engagedDate != null && engagedDate !== '') {
        analyticsDebugLog('handleConfirmationPage() path: 3');
        resetLocalStorageData();
        logAnalytic(
          {
            category: 'engagement_conversion_sale',
            potentialTotalValue: safeGetDollarValue(dollarValues, 'highestValue'),
            allDollarValuesOnPage: safeGetDollarValue(dollarValues, 'dollarArray'),
            interactedDate: convertDateToBigqueryFormat(engagedDate),
            currency: currency || 'USD',
            sessionID: hovrSessionId,
            thirtyDayTrackingID,
            backupSiteId: siteId,
          },
          siteInfo,
        );
        logToGoogleAnalytics(
          'hovr_interacted_checkout',
          {
            hovrTotalValue: safeGetDollarValue(dollarValues, 'highestValue'),
          },
        );
      } else {
        analyticsDebugLog('handleConfirmationPage() path: 4');
        resetLocalStorageData();
        logAnalytic(
          {
            category: 'engagement_conversion_sale',
            potentialTotalValue: safeGetDollarValue(dollarValues, 'highestValue'),
            allDollarValuesOnPage: safeGetDollarValue(dollarValues, 'dollarArray'),
            sessionID: hovrSessionId,
            currency: currency || 'USD',
            thirtyDayTrackingID,
            backupSiteId: siteId,
          },
          siteInfo,
        );
        logToGoogleAnalytics(
          'hovr_interacted_checkout',
          {
            hovrTotalValue: safeGetDollarValue(dollarValues, 'highestValue'),
          },
        );
      }
    } else if (dollarValues?.highestValue
      && isNumber(dollarValues.highestValue)
      && dollarValues.highestValue > 0) { // Log checkout anyway to see performance
      analyticsDebugLog('handleConfirmationPage() path: 5');
      logAnalytic({
        potentialTotalValue: safeGetDollarValue(dollarValues, 'highestValue'),
        allDollarValuesOnPage: safeGetDollarValue(dollarValues, 'dollarArray'),
        category: 'no_interaction_checkout',
        currency: currency || 'USD',
        sessionID: hovrSessionId,
        thirtyDayTrackingID,
        backupSiteId: siteId,
      }, siteInfo);
    } else {
      analyticsDebugLog('handleConfirmationPage() path: 6');
      logAnalytic({
        category: 'no_interaction_checkout',
        currency: currency || 'USD',
        sessionID: hovrSessionId,
        thirtyDayTrackingID,
        backupSiteId: siteId,
      }, siteInfo);
    }
  } catch (error) {
    analyticsDebugLog('handleConfirmationPage() path: 7');
    analyticsDebugLog('Error in handleConfirmationPage(): ', error);
    logAnalytic(
      {
        category: 'error',
        message: JSON.stringify(error),
      },
      siteInfo,
    );
  }
}

function isConfirmationPage(siteInfo: SiteInfo, engageValues: EngageValues): void {
  handleConfirmationPage(siteInfo, engageValues);
  const urlToUse = window.location.origin;
  const pixelRef = document.getElementById('hovr-pixel') as HTMLIFrameElement;
  if (pixelRef?.contentWindow != null) {
    pixelRef.contentWindow.postMessage({ clearCookies: true }, urlToUse);
  }
}
const excludeURLs = ['bookingdetails']; // monarch reduce over tracking

async function checkDataLayerForConfirmationEvent(tryCount = 0): Promise<boolean> {
  const retries = 1; // Number of retries to check the data layer
  const waitTime = 5000; // 5 seconds
  // @ts-ignore
  const { dataLayer } = window;
  if (!dataLayer) {
    return false;
  }
  const confirmationEvents = ['purchase', 'transactioncomplete', 'bookingcomplete', 'ordercomplete', 'confirmation'];
  for (const i in dataLayer) {
    const urlContainsConfirmation = excludeURLs.some(
      (part) => window.location.href.toLowerCase().includes(part),
    );
    // If on the confirmation page
    if (confirmationEvents.includes(dataLayer[i]?.event) && !urlContainsConfirmation) {
      const event: SynxisDataLayerCheckoutEvent = dataLayer[i];
      analyticsDebugLog('Data layer confirmation event found: ', event);
      if ((window.location.hostname === 'be.synxis.com'
        || window.sbe_logMetrics != null )
        && window.location.pathname.includes('confirm')
      ) {
        return true;
      } if ((window.location.hostname === 'be.synxis.com'
        || window.sbe_logMetrics != null )
        && !window.location.pathname.includes('confirm')) {
        return false;
      }
      return true;
    }
  }
  if (tryCount >= retries) {
    return false;
  }
  // wait and check again
  await new Promise((resolve) => { setTimeout(resolve, waitTime); });
  return checkDataLayerForConfirmationEvent(tryCount + 1);
}

// eslint-disable-next-line max-len
export async function checkIfURLContainsConfirmation(siteInfo: SiteInfo, engageValues: EngageValues): Promise<void> {
  const {
    pathname, hostname,
  } = window.location;
  
  const urlPartsToCheck = [
    'confirmation',
    'confirm',
    'complete',
    'booking-complete',
    'resconfirm',
  ];
  const urlContainsConfirmation = urlPartsToCheck.some(
    (part) => window.location.href.toLowerCase().includes(part),
  );

  if (window.location.hostname.endsWith('webrez.com')
    && window.top !== window.self
  ) {
    analyticsDebugLog('Webrez iframe detected, checking page text for confirmation indicators.');
    analyticsDebugLog('Webrez iframe siteInfo values: ', siteInfo);
    analyticsDebugLog('Webrez iframe engageValues values: ', engageValues);
    const pageText = document.body.textContent.toLowerCase();
    if (((pageText.includes('reservation summary') && pageText.includes('confirmation'))
      || pageText.includes('confirmation #')
      && hostname !== 'bookingengine2.sandos.com')
    ) {
      isConfirmationPage(siteInfo, engageValues);
    }
    return;
  }
  if ((hostname === 'bookingengine2.sandos.com') && pathname.includes('/thanks')) {
    // Wait for dollar amount to appear in DOM using synchronous check
    const waitForDollarAmount = (): boolean => {
      const currencyMap: CurrencyValues | null = findCurrencyValuesSync();
      if (currencyMap && currencyMap.highestValue) {
        isConfirmationPage(siteInfo, engageValues);
        return true;
      }
      return false;
    };

    if (!waitForDollarAmount()) {
      const observer = new MutationObserver(() => {
        if (waitForDollarAmount()) {
          observer.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    }
  } else if (((urlContainsConfirmation
    || siteInfo.confirmationPage === window.location.origin + window.location.pathname)
     && hostname !== 'bookingengine2.sandos.com')
  ) {
    isConfirmationPage(siteInfo, engageValues);
    return;
  }

  // If url check didn't work, check the data layer for confirmation events
  const confirmationEventFound = await checkDataLayerForConfirmationEvent();
  if (confirmationEventFound) {
    isConfirmationPage(siteInfo, engageValues);
  }
}

/**
 * Post message listener from booking pixel
 */
export function initPixelPostMessageListener(siteInfo: SiteInfo, fireLogicWithSiteId: (siteId: string | null, hotelParam: string | null) => void): void {
  window.addEventListener(
    'message',
    async (event) => {
      if (event.origin === config.BOOKING_PIXEL || event.origin === config.BOOKING_PIXEL_WWW) {
        const eventData: TrackedInfo = event.data;
        if (eventData?.hovrEngaged) {
          // Store on local storage since it's easier to manage and access
          localStorage.setItem('hovrEngaged', 'true');
          localStorage.setItem('hovrEngagedDate', eventData.hovrEngagedDate);
          localStorage.setItem('hovrSessionId', eventData.hovrSessionId);
          localStorage.setItem('hovrThirtyDayTrackingID', eventData.thirtyDayTrackingID);
          localStorage.setItem('siteId', eventData.siteId);
        }
        if (siteInfo != null) {
          checkIfURLContainsConfirmation(siteInfo, {
            hovrEngaged: eventData?.hovrEngaged,
            hovrEngagedDate: eventData?.hovrEngagedDate,
            hovrSessionId: eventData?.hovrSessionId,
            thirtyDayTrackingID: eventData?.thirtyDayTrackingID,
            siteId: eventData?.siteId,
          });
        } else {
          fireLogicWithSiteId(eventData.siteId, '');
        }
      }
    },
    false,
  );
}

/**
 * Injects the booking pixel into the page
 * @param siteId Site ID to inject the booking pixel for
 */
export function injectBookingPixel(siteId: string, hotelParam: string | null): void {
  const newPixel = document.createElement('iframe');
  newPixel.title = 'hovr pixel';
  newPixel.id = 'hovr-pixel';
  newPixel.src = `${config.HOTEL_PARAM_WORKAROUND.includes(hotelParam) ? config.BOOKING_PIXEL_WWW : config.BOOKING_PIXEL}/index.html?fromHovr=false&siteId=${siteId}`;
  newPixel.style.width = '0px';
  newPixel.style.height = '0px';
  newPixel.style.borderWidth = '0px';
  document.body.appendChild(newPixel);
}

export function logInitialPageLoad(siteInfo: SiteInfo) {
  const direcCTACookie: string = getCookie('hovrDirectCTA') as string;
  const hovrEngagedCookie = getCookie('hovrEngaged');
  let initalLoadWithEngagement = false;
  if ((direcCTACookie != null && direcCTACookie !== '') || (hovrEngagedCookie != null && hovrEngagedCookie !== '')) {
    initalLoadWithEngagement = true;
  }
  logAnalytic(
    {
      category: 'pageload',
      interactedWithHovr: initalLoadWithEngagement,
    },
    siteInfo,
    true, // allow multiple logs
  );
}
/**
 * Data layer event listeners
 * @param event Event data
 * @param eventName Event name
 */
function dataLayerListeners(event: any, eventName: string) {
  dataLayerDebugLog('Datalayer listener: ', event);
  switch (eventName) {
    case 'confirmation':
      // !TODO: need to rethink this handler since it can double log
      // with the confirmation page with a race condition
      // maybe a flag marking that it was logged? or just if the localstorage data exists
      break;
    default:
      dataLayerDebugLog('Data layer event not supported by hovr: ', eventName);
  }
}

/**
 * Listen to changes in the page's data layer
 */
export function registerDataLayerEvents(): void {
  // @ts-ignore
  if (window?.dataStream != null) {
    dataLayerDebugLog('Launching data layer subscription');
    // @ts-ignore
    window.dataStream.subscribe(
      'quantummetric',
      ['confirmation'],
      dataLayerListeners,
    );
  }
}

export default {
  checkForDirectCTA,
  checkIsConfirmationPage: handleConfirmationPage,
  fetchSiteInformation,
  injectBookingPixel,
  checkIfURLContainsConfirmation,
  getCookie,
};
